import { useEffect } from 'react';
import { useAuth } from '../hooks/use-auth';
import { useIsAdmin, useIsAssignedTasksOnly, usePermission } from '../hooks/use-permission';
import { prefetchRoute, type RouteName } from '../routes/lazy-routes';

/**
 * Warms route chunks during time the user is already waiting.
 *
 * Code-splitting trades a smaller entry bundle for an extra request on first
 * visit to each route. This reclaims that trade by fetching the likely-next
 * chunk while nothing else is competing for the network:
 *
 *  - Signed out, sitting on /login: `/home` is the guaranteed next screen, and
 *    the user is busy typing. By the time they submit, the chunk is cached.
 *  - Signed in: session restoration and the first data fetches are already in
 *    flight (each API read costs a ~258 ms Supabase round-trip), so a parallel
 *    chunk download is effectively free wall-clock.
 *
 * `prefetchRoute` dedupes via the module registry, so repeated calls are cheap.
 *
 * Two rules keep this from cannibalising the load it is supposed to hide behind:
 *
 *  1. Wait for the window `load` event first. `requestIdleCallback` measures
 *     *main-thread* idle, not network idle — it fires as soon as React finishes
 *     its first commit, which on a throttled connection is well before the
 *     current route's own chunks and API reads are done. Prefetching there put
 *     ~35 chunk requests on the wire at ~680 ms and pushed the page's own API
 *     calls out to ~1.06 s.
 *  2. Warm one route at a time. Each route chunk pulls a fan-out of shared
 *     chunks, so six parallel `prefetchRoute` calls is dozens of parallel
 *     requests competing for the same connection pool.
 */

type IdleHandle = number;

function onIdle(fn: () => void): IdleHandle {
  if (typeof window.requestIdleCallback === 'function') {
    return window.requestIdleCallback(fn, { timeout: 2_000 });
  }
  return window.setTimeout(fn, 200);
}

function cancelIdle(handle: IdleHandle): void {
  if (typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(handle);
    return;
  }
  window.clearTimeout(handle);
}

/** Resolves once the page has finished loading (immediately if it already has). */
function afterWindowLoad(fn: () => void): () => void {
  if (document.readyState === 'complete') {
    fn();
    return () => {};
  }
  window.addEventListener('load', fn, { once: true });
  return () => window.removeEventListener('load', fn);
}

export function RoutePrefetcher() {
  const { isAuthenticated, isLoading } = useAuth();
  const isAdmin = useIsAdmin();
  const assignedTasksOnly = useIsAssignedTasksOnly();
  const canViewReports = usePermission('view_reports');
  const canViewTeam = usePermission('view_resource_load');

  useEffect(() => {
    if (isLoading) return;

    // `/home` is the landing route for every authenticated role, so it is worth
    // warming whether or not the user has signed in yet.
    const targets: RouteName[] = ['home'];

    if (isAuthenticated) {
      // Small, high-probability destinations only. Each is a few kB.
      targets.push('projects', 'myTasks', 'notifications');

      // Only warm chunks the user can actually reach — prefetching a route that
      // AuthGuard will redirect away from is pure waste.
      if (isAdmin || (canViewReports && !assignedTasksOnly)) targets.push('reports');
      if (isAdmin || (canViewTeam && !assignedTasksOnly)) targets.push('team');

      // Dashboard is deliberately NOT here. Its chunk statically imports the
      // charting stack (~385 kB gzip), and `/home` — not `/dashboard` — is the
      // landing route for every role, so speculatively pulling it on every page
      // load would spend more bandwidth than the whole rest of the app for a
      // screen the user may never open. The sidebar warms it on hover instead,
      // which is early enough to hide the load behind the pointer travel.
    }

    let cancelled = false;
    let handle: IdleHandle | null = null;

    // One at a time, each in its own idle slot, so a long prefetch queue never
    // competes with the current screen or blocks the main thread in one burst.
    const warmNext = (index: number) => {
      if (cancelled || index >= targets.length) return;
      handle = onIdle(() => {
        void prefetchRoute(targets[index]).then(() => warmNext(index + 1));
      });
    };

    const removeLoadListener = afterWindowLoad(() => warmNext(0));

    return () => {
      cancelled = true;
      removeLoadListener();
      if (handle !== null) cancelIdle(handle);
    };
  }, [isAuthenticated, isLoading, isAdmin, assignedTasksOnly, canViewReports, canViewTeam]);

  // Warm the exact destination as soon as intent is visible. This hides the
  // route-chunk request behind pointer travel and keyboard focus for every
  // internal link, including dynamic project/task pages.
  useEffect(() => {
    const warm = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href') ?? '';
      const name = routeNameForHref(href);
      if (name) void prefetchRoute(name);
    };
    document.addEventListener('pointerover', warm, { passive: true });
    document.addEventListener('focusin', warm);
    return () => {
      document.removeEventListener('pointerover', warm);
      document.removeEventListener('focusin', warm);
    };
  }, []);

  return null;
}

function routeNameForHref(href: string): RouteName | null {
  if (!href.startsWith('/') || href.startsWith('//')) return null;
  const path = href.split(/[?#]/, 1)[0];
  if (path === '/home') return 'home';
  if (path === '/dashboard') return 'dashboard';
  if (path === '/my-tasks') return 'myTasks';
  if (path === '/projects') return 'projects';
  if (path === '/projects/new') return 'projectNew';
  if (/^\/projects\/[^/]+\/forms\/[^/]+$/.test(path)) return 'projectForm';
  if (/^\/projects\/[^/]+\/tasks\/new$/.test(path)) return 'taskNew';
  if (/^\/projects\/[^/]+\/tasks\/[^/]+$/.test(path)) return 'taskDetail';
  if (/^\/projects\/[^/]+$/.test(path)) return 'projectDetail';
  if (path === '/notifications') return 'notifications';
  if (path === '/team') return 'team';
  if (path === '/reports') return 'reports';
  if (path === '/settings') return 'settings';
  if (path === '/users') return 'users';
  if (path === '/users/invite') return 'userInvite';
  if (/^\/users\/[^/]+$/.test(path)) return 'userDetail';
  if (path === '/form-templates') return 'formTemplates';
  if (/^\/form-templates\/[^/]+$/.test(path)) return 'formTemplateBuilder';
  if (path === '/roles') return 'roles';
  if (path === '/roles/new') return 'roleNew';
  if (/^\/roles\/[^/]+$/.test(path)) return 'roleDetail';
  return null;
}
